import React, { useEffect, useMemo, useState } from 'react';
import Card from '../../UI/Card/Card';
import userService from '../../app/services/user.service';
import { toast } from 'react-toastify';
import Loader from '../../UI/loader/loader';
import { Link, useNavigate } from 'react-router-dom';
import SecBTN from '../../UI/SecBTN';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
/**
 * Step 3: Duplicate Check
 * - Fetches final saved questions for a given upload_id
 * - Shows them in a simple paginated table
 */
const DuplicateCheck = ({ uploadId, onPrevious, onNext,GoToTable }) => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (!uploadId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await userService.getFinalSavedQuestionList(uploadId);

        // Try common property names, fall back to empty array
        const list =
          response?.final_generated_questionlist ?? [];

        setQuestions(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error fetching final saved questions:', err);
        toast.error('Failed to load final saved questions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uploadId]);

  const totalPages = Math.max(1, Math.ceil(questions.length / pageSize));


  const pagedQuestions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return questions.slice(start, start + pageSize);
  }, [questions, currentPage]);

  if (!uploadId) {
    return (
      <Card className="mb-4">
        <div className="p-4 text-center">
          <p>Please select an upload to view its questions.</p>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="mb-4">
        <div className="text-center p-5">
          <Loader />
          <p className="mt-3">Loading final saved questions...</p>
        </div>
      </Card>
    );
  }

  const handleDownload = async () => {
    try {
      setLoading(true);
  
      const payload = {
        upload_id: uploadId,
      };
  
      const response = await userService.DownloadQuestionIngestion(payload);
  
      if (response?.info !== 200 || !response?.result) {
        toast.error("No data available to download");
        return;
      }
  
      const data = response.result;
  
      // Convert JSON to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
  
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "DuplicateCheck");
  
      // Generate Excel file buffer
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
  
      // Create Blob
      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
  
      // Download file
      saveAs(fileData, `Generated_QID_${uploadId}.xlsx`);
  
      toast.success("Excel downloaded successfully");
    } catch (err) {
      console.error(err);
      toast.error("Download failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">QID List</h5>
          <p className="mb-0 text-muted">
            Upload ID: <strong>{uploadId}</strong> | Total Questions:{' '}
            <strong>{questions.length}</strong>
          </p>
        </div>

        <div className="d-flex gap-2">
          <SecBTN className="sec-btn" 
          onClick={handleDownload}
          >
            Download
          </SecBTN>
          {onPrevious && (
            <button className="btn btn-outline-secondary btn-sm" onClick={GoToTable}>
              Go to table
            </button>
          )}
          
        </div>
      </div>

      <Card className="mb-4">
        <div className="table-responsive">
          <table className="table table-bordered mb-0">
            <thead>
              <tr>
                <th style={{ width: 60 }}>SNo</th>
                <th style={{ width: 120 }}>QID</th>
                <th>Q_Text</th>
                <th>Subject Name</th>
                <th>Topic Name</th>
                <th>Sub Topic Name</th>
                <th>Type</th>
                <th>action</th>
              </tr>
            </thead>
            <tbody>
              {pagedQuestions.length > 0 ? (
                pagedQuestions.map((q, index) => (
                  <tr key={q.qid ?? index}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>{q.qid ?? '-'}</td>
                    <td>
                      {q.q_text && (
                        <div
                          dangerouslySetInnerHTML={{ __html: q.q_text }}
                        />
                      )}
                    </td>
                    <td>{q.sub_name}</td>
                    <td>{q.topic_name}</td>
                    <td>{q.sub_topic_name}</td>
                    <td>{q.type}</td>
                    <td><Link to={`/editQuestion/edit/${q.qid}`} target='_blank'><SecBTN className='sec-btn1'>Edit</SecBTN></Link></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center">
                    No questions found for this upload
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {questions.length > pageSize && (
        <div className="d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages, p + 1))
            }
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};

export default DuplicateCheck;
